import { useState } from "react";

import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { Button } from "@/design-system/Button";
import { Stack } from "@/design-system/Stack";
import { isHttpError } from "@/helpers/Network/errors";
import { servicePartnerMutations } from "@/queries/service-partners/mutations";
import { useTranslation } from "@/translations";

interface IntegrationAccessButtonProps {
  servicePartnerId: string;
  label: string;
  returnUrl?: string;
}

export function IntegrationAccessButton({
  servicePartnerId,
  label,
  returnUrl,
}: IntegrationAccessButtonProps): React.ReactNode {
  const { t } = useTranslation();
  const showFlashToast = useFlashToast();
  const [isUnavailable, setIsUnavailable] = useState(false);
  const accessLinkMutation = servicePartnerMutations.useGetAccessLink();

  function handleAccess() {
    accessLinkMutation.mutate(
      { servicePartnerId, returnUrl },
      {
        onSuccess(accessLink) {
          setIsUnavailable(false);
          window.open(accessLink.url, "_blank", "noopener,noreferrer");
        },
        onError(error) {
          const integrationUnavailable = isHttpError(error) && error.status === 409;
          setIsUnavailable(integrationUnavailable);

          if (!integrationUnavailable) {
            showFlashToast({ variant: "alert", message: t("common.generic-error") });
          }
        },
      },
    );
  }

  return (
    <Stack gap="sm" align="start">
      <Button variant="filled" isLoading={accessLinkMutation.isPending} onClick={handleAccess}>
        {label}
      </Button>
      {isUnavailable && (
        <p className="text-caption text-text-secondary">{t("page.service-details.integration.unavailable")}</p>
      )}
    </Stack>
  );
}
